import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LifebuoyIcon, PaperAirplaneIcon, PhotoIcon } from "@heroicons/react/24/outline";
import { AXIOS_GET } from "../../server/api/crud";
import { getUser, getUserdetails } from "../../Localstorage/localstorage";
import {
  assignSupportTicketAdmin,
  createSupportTicket,
  fetchSupportTicketById,
  fetchSupportTickets,
  markSupportTicketRead,
  sendSupportMessage,
  updateSupportTicketStatus,
} from "../../apiCalls/supportApi";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "../../firebase/firebase";

const STATUS_OPTIONS = [
  { id: "open", label: "Open" },
  { id: "in_progress", label: "In behandeling" },
  { id: "waiting_customer", label: "Wacht op klant" },
  { id: "resolved", label: "Opgelost" },
  { id: "closed", label: "Gesloten" },
];

const PRIORITY_OPTIONS = [
  { id: "low", label: "Laag" },
  { id: "normal", label: "Normaal" },
  { id: "high", label: "Hoog" },
  { id: "urgent", label: "Urgent" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function isImageUrl(url, attachmentType) {
  if (!url) return false;
  if (String(attachmentType || "").startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(url).split("?")[0]);
}

const SupportCenter = () => {
  const user = getUser();
  const userdetails = getUserdetails();
  const isAdmin = Number(user?.role) === 1;
  const scrollRef = useRef(null);
  const uploadInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [showCreateTicket, setShowCreateTicket] = useState(false);

  const [newTicketSubject, setNewTicketSubject] = useState("");
  const [newTicketBody, setNewTicketBody] = useState("");
  const [newTicketPriority, setNewTicketPriority] = useState("normal");
  const [newTicketAttachmentUrl, setNewTicketAttachmentUrl] = useState("");
  const [newTicketAttachmentType, setNewTicketAttachmentType] = useState("");

  const [messageBody, setMessageBody] = useState("");
  const [messageAttachmentUrl, setMessageAttachmentUrl] = useState("");
  const [messageAttachmentType, setMessageAttachmentType] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);

  const refreshTickets = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchSupportTickets();
      setTickets(list || []);
      if (!selectedTicketId && list?.length > 0) {
        setSelectedTicketId(list[0].id);
      } else if (selectedTicketId && !list?.find((t) => t.id === selectedTicketId)) {
        setSelectedTicketId(list?.[0]?.id || null);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedTicketId]);

  useEffect(() => {
    refreshTickets();
  }, [refreshTickets]);

  useEffect(() => {
    if (!isAdmin) return;
    AXIOS_GET("users/all/admins")
      .then((resp) => setAdmins(resp?.data?.data || []))
      .catch(() => setAdmins([]));
  }, [isAdmin]);

  const loadTicket = useCallback(
    async (ticketId) => {
      if (!ticketId) {
        setSelectedTicket(null);
        setMessages([]);
        return;
      }
      const data = await fetchSupportTicketById(ticketId);
      setSelectedTicket(data?.ticket || null);
      setMessages(data?.messages || []);
      await markSupportTicketRead(ticketId);
      await refreshTickets();
    },
    [refreshTickets]
  );

  useEffect(() => {
    loadTicket(selectedTicketId);
  }, [selectedTicketId, loadTicket]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [messages.length]);

  const unreadForTicket = (ticket) =>
    isAdmin ? Number(ticket?.unread_admin || 0) : Number(ticket?.unread_customer || 0);

  const handleUploadAttachment = async (event, type = "message") => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const safeName = String(file.name || "attachment").replace(/\s+/g, "_");
      const folder = `${userdetails?.full_name || "support"}/${user?.id || "unknown"}/support/${selectedTicketId || "new"}`;
      const fileRef = ref(storage, `${folder}/${Date.now()}-${safeName}`);
      const uploadTask = uploadBytesResumable(fileRef, file);
      await new Promise((resolve, reject) => {
        uploadTask.on("state_changed", () => {}, reject, resolve);
      });
      const url = await getDownloadURL(uploadTask.snapshot.ref);
      if (type === "create") {
        setNewTicketAttachmentUrl(url);
        setNewTicketAttachmentType(file.type || null);
      } else {
        setMessageAttachmentUrl(url);
        setMessageAttachmentType(file.type || null);
      }
    } catch (error) {
      console.error("Support attachment upload failed:", error);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicketSubject.trim()) return;
    if (!newTicketBody.trim() && !newTicketAttachmentUrl) return;
    setSending(true);
    try {
      const created = await createSupportTicket({
        subject: newTicketSubject.trim(),
        body: newTicketBody.trim(),
        priority: newTicketPriority,
        attachment_url: newTicketAttachmentUrl || null,
        attachment_type: newTicketAttachmentType || null,
      });
      setShowCreateTicket(false);
      setNewTicketSubject("");
      setNewTicketBody("");
      setNewTicketPriority("normal");
      setNewTicketAttachmentUrl("");
      setNewTicketAttachmentType("");
      await refreshTickets();
      if (created?.ticket?.id) {
        setSelectedTicketId(created.ticket.id);
      }
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedTicketId) return;
    if (!messageBody.trim() && !messageAttachmentUrl) return;
    setSending(true);
    try {
      const data = await sendSupportMessage(selectedTicketId, {
        body: messageBody.trim(),
        attachment_url: messageAttachmentUrl || null,
        attachment_type: messageAttachmentType || null,
      });
      setSelectedTicket(data?.ticket || selectedTicket);
      setMessages(data?.messages || []);
      setMessageBody("");
      setMessageAttachmentUrl("");
      setMessageAttachmentType("");
      await refreshTickets();
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (nextStatus) => {
    if (!selectedTicketId || !isAdmin) return;
    const updated = await updateSupportTicketStatus(selectedTicketId, nextStatus);
    if (updated) setSelectedTicket(updated);
    await refreshTickets();
  };

  const handleAssignAdmin = async (adminId) => {
    if (!selectedTicketId || !isAdmin) return;
    const updated = await assignSupportTicketAdmin(selectedTicketId, adminId);
    if (updated) setSelectedTicket(updated);
    await refreshTickets();
  };

  const ticketRows = useMemo(() => {
    if (!tickets?.length) return null;
    return tickets.map((ticket) => {
      const active = ticket.id === selectedTicketId;
      const unread = unreadForTicket(ticket);
      return (
        <li key={ticket.id} className="px-2 py-1.5">
          <button
            type="button"
            onClick={() => setSelectedTicketId(ticket.id)}
            className={classNames(
              "flex w-full items-start gap-3 rounded-xl border px-3 py-3 text-left transition",
              active ? "border-wa-accent/45 bg-wa-accent/10" : "border-transparent hover:border-wa-border hover:bg-wa-hover"
            )}
          >
            <div className="mt-0.5 rounded-full bg-wa-panel2 p-2 text-wa-accent">
              <LifebuoyIcon className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className={classNames("truncate text-sm font-semibold", unread > 0 ? "text-wa-text" : "text-wa-text/85")}>
                  {ticket.subject}
                </p>
                <time className="text-xs text-wa-muted">
                  {ticket?.last_message_at ? moment(ticket.last_message_at).format("HH:mm") : "—"}
                </time>
              </div>
              <p className="mt-0.5 text-xs text-wa-muted">
                {ticket.status} · {ticket.priority}
              </p>
            </div>
            {unread > 0 ? (
              <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-wa-accent px-1 text-xs font-semibold text-wa-onaccent">
                {unread > 99 ? "99+" : unread}
              </span>
            ) : null}
          </button>
        </li>
      );
    });
  }, [tickets, selectedTicketId, isAdmin]);

  return (
    <div className="flex h-[calc(100dvh-4rem)] min-h-0 w-full flex-col bg-wa-app text-wa-text md:flex-row">
      <aside
        className={classNames(
          "h-full w-full flex-col border-r border-wa-border bg-wa-panel md:flex md:max-w-[360px]",
          selectedTicket && "hidden md:flex"
        )}
      >
        <header className="flex items-center justify-between border-b border-wa-border px-3 py-3">
          <div>
            <h1 className="text-base font-semibold">Support</h1>
            <p className="text-xs text-wa-muted">{tickets.length} tickets</p>
          </div>
          {!isAdmin ? (
            <button
              type="button"
              onClick={() => setShowCreateTicket((prev) => !prev)}
              className="rounded-lg border border-wa-border bg-wa-panel2 px-2.5 py-1.5 text-xs font-semibold hover:bg-wa-hover"
            >
              Nieuw ticket
            </button>
          ) : null}
        </header>

        {showCreateTicket ? (
          <div className="space-y-2 border-b border-wa-border bg-wa-panel2 p-3">
            <input
              value={newTicketSubject}
              onChange={(e) => setNewTicketSubject(e.target.value)}
              placeholder="Onderwerp"
              className="w-full rounded-lg border border-wa-border bg-wa-panel px-3 py-2 text-sm outline-none focus:border-wa-accent"
            />
            <textarea
              value={newTicketBody}
              onChange={(e) => setNewTicketBody(e.target.value)}
              placeholder="Beschrijf je vraag..."
              rows={3}
              className="w-full rounded-lg border border-wa-border bg-wa-panel px-3 py-2 text-sm outline-none focus:border-wa-accent"
            />
            <div className="flex items-center gap-2">
              <select
                value={newTicketPriority}
                onChange={(e) => setNewTicketPriority(e.target.value)}
                className="flex-1 rounded-lg border border-wa-border bg-wa-panel px-3 py-2 text-sm outline-none focus:border-wa-accent"
              >
                {PRIORITY_OPTIONS.map((priority) => (
                  <option key={priority.id} value={priority.id}>
                    {priority.label}
                  </option>
                ))}
              </select>
              <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-wa-border bg-wa-panel px-2.5 py-2 text-xs hover:bg-wa-hover">
                <PhotoIcon className="h-4 w-4" />
                Bijlage
                <input type="file" className="hidden" onChange={(event) => handleUploadAttachment(event, "create")} />
              </label>
            </div>
            {newTicketAttachmentUrl ? (
              <p className="text-xs text-wa-accent">Bijlage klaar om mee te sturen.</p>
            ) : null}
            <button
              type="button"
              disabled={sending || uploading}
              onClick={handleCreateTicket}
              className="w-full rounded-lg bg-wa-accent px-3 py-2 text-sm font-semibold text-wa-onaccent disabled:cursor-not-allowed disabled:opacity-60"
            >
              Ticket aanmaken
            </button>
          </div>
        ) : null}

        <ul className="min-h-0 flex-1 overflow-y-auto py-2">{ticketRows}</ul>
        {loading ? <p className="px-4 pb-3 text-xs text-wa-muted">Laden...</p> : null}
      </aside>

      <section
        className={classNames(
          "min-h-0 flex-1 flex-col bg-wa-app",
          selectedTicket ? "flex" : "hidden md:flex"
        )}
      >
        {selectedTicket ? (
          <>
            <header className="flex flex-wrap items-center gap-2 border-b border-wa-border bg-wa-panel/60 px-4 py-3">
              <button
                type="button"
                onClick={() => setSelectedTicketId(null)}
                className="rounded-lg border border-wa-border bg-wa-panel px-2 py-1 text-xs md:hidden"
              >
                Terug
              </button>
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-base font-semibold">{selectedTicket.subject}</h2>
                <p className="text-xs text-wa-muted">
                  Ticket ID: {selectedTicket.id} · Laatste update:{" "}
                  {selectedTicket.last_message_at ? moment(selectedTicket.last_message_at).format("DD/MM HH:mm") : "—"}
                </p>
              </div>
              {isAdmin ? (
                <>
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="rounded-lg border border-wa-border bg-wa-panel px-2.5 py-1.5 text-xs outline-none focus:border-wa-accent"
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedTicket.assigned_admin_id || ""}
                    onChange={(e) => handleAssignAdmin(e.target.value)}
                    className="rounded-lg border border-wa-border bg-wa-panel px-2.5 py-1.5 text-xs outline-none focus:border-wa-accent"
                  >
                    <option value="">Geen toewijzing</option>
                    {admins.map((admin) => (
                      <option key={admin.id || admin.user_id} value={admin.id || admin.user_id}>
                        {admin.full_name || admin.username}
                      </option>
                    ))}
                  </select>
                </>
              ) : null}
            </header>

            <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-wa-app px-4 py-4">
              {messages.map((item) => {
                const mine = String(item.author_id) === String(user?.id);
                return (
                  <article key={item.id} className={classNames("max-w-[86%] rounded-2xl border px-3 py-2 shadow-sm", mine ? "ml-auto border-wa-accent/30 bg-wa-accent/15" : "border-wa-border bg-wa-panel")}>
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-wa-muted">
                      {item.author_role === "admin" ? "Support" : "Klant"}
                    </p>
                    {item.body ? <p className="whitespace-pre-wrap text-sm">{item.body}</p> : null}
                    {item.attachment_url ? (
                      <div className="mt-2">
                        {isImageUrl(item.attachment_url, item.attachment_type) ? (
                          <a href={item.attachment_url} target="_blank" rel="noreferrer">
                            <img src={item.attachment_url} alt="Bijlage" className="max-h-64 rounded-lg border border-wa-border object-cover" />
                          </a>
                        ) : (
                          <a href={item.attachment_url} target="_blank" rel="noreferrer" className="text-sm font-medium text-wa-accent underline">
                            Bijlage openen
                          </a>
                        )}
                      </div>
                    ) : null}
                    <p className="mt-1 text-right text-[11px] text-wa-muted">
                      {item.created_at ? moment(item.created_at).format("DD/MM HH:mm") : ""}
                    </p>
                  </article>
                );
              })}
            </div>

            <footer className="border-t border-wa-border bg-wa-panel px-3 py-3">
              <div className="flex items-end gap-2">
                <input
                  ref={uploadInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleUploadAttachment}
                />
                <button
                  type="button"
                  onClick={() => uploadInputRef.current?.click()}
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-wa-border bg-wa-panel2 text-wa-muted hover:text-wa-text"
                >
                  <PhotoIcon className="h-5 w-5" />
                </button>
                <textarea
                  rows={1}
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  placeholder="Typ je bericht..."
                  className="max-h-36 min-h-[44px] flex-1 rounded-xl border border-wa-border bg-wa-panel2 px-3 py-2 text-sm outline-none focus:border-wa-accent"
                />
                <button
                  type="button"
                  disabled={sending || uploading}
                  onClick={handleSendMessage}
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-wa-accent text-wa-onaccent disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
              {messageAttachmentUrl ? (
                <p className="mt-2 text-xs text-wa-accent">Bijlage toegevoegd aan je volgende bericht.</p>
              ) : null}
            </footer>
          </>
        ) : (
          <div className="flex h-full items-center justify-center p-6 text-center text-sm text-wa-muted">
            Selecteer een ticket om de conversatie te openen.
          </div>
        )}
      </section>
    </div>
  );
};

export default SupportCenter;
